import React, { memo, useMemo, useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import {
  PayPalScriptProvider,
  PayPalButtons,
  usePayPalScriptReducer,
} from '@paypal/react-paypal-js';
import Shimmer from './shimmer';
import templateData from '../../utility/getTemplateData';
import { createOrder } from './createOrder';
import { captureOrder } from './captureOrder';
import { onInit, onClick, onCancel, onError, InEligibleError } from './common';

/**
 * Style configuration for the Venmo payment button.
 * Defines the appearance and shape of the button.
 */
const BUTTON_STYLE = {
  label: 'donate',
  shape: 'pill',
};

/**
 * DonateWithVenmo Component
 *
 * A React component that renders a Venmo payment button integrated with PayPal SDK.
 * This component handles the complete Venmo donation flow including:
 * - PayPal SDK initialization and configuration
 * - Loading states with shimmer effect
 * - Order creation and capture
 * - Error handling and user feedback
 * - Analytics tracking for all payment events
 * - Eligibility checking for Venmo payments
 *
 * The component is memoized for performance optimization to prevent unnecessary re-renders.
 *
 * @param {Object} props - Component props
 * @param {Function} props.handleAlert - Callback function to display alert messages to users
 * @param {Function} props.validateDonationAmount - Function to validate donation amount before processing
 * @returns {React.Component|null} The Venmo payment button component or null if ineligible
 */
const DonateWithVenmo = memo(({ handleAlert, validateDonationAmount }) => {
  // React Router history for navigation after payment completion
  const history = useHistory();

  // Loading state to control shimmer display while PayPal SDK initializes
  const [isLoading, setIsLoading] = useState(true);

  // Eligibility state to determine if Venmo payments are available for this user
  const [isEligible, setIsEligible] = useState(true);

  // SDK rejection state to track if PayPal SDK failed to load
  const [isSdkRejected, setIsSdkRejected] = useState(false);

  /**
   * Memoized PayPal SDK configuration options.
   * Combines template data configuration with CSP nonce for security.
   *
   * `merchantDomain` is deliberately excluded here - it's a <PayPalButtons>-level
   * prop, not a script-load option, and would otherwise get serialized onto the
   * SDK script src as a stray query param.
   */
  const sdkOptions = useMemo(() => {
    // eslint-disable-next-line no-unused-vars -- destructured only to exclude it from scriptOptions
    const { merchantDomain, ...scriptOptions } = templateData.venmoButtonConfig || {};
    return scriptOptions;
  }, []);

  /**
   * The real merchant domain the buyer originated from, captured server-side
   * from the inbound Referer header (donatenodeweb is PayPal-hosted, so the SDK's
   * own `window.location.origin` capture would otherwise report a paypal.com
   * domain instead of the merchant's). Undefined when unavailable/invalid, in
   * which case the prop is simply omitted below.
   */
  const merchantDomain = templateData.venmoButtonConfig?.merchantDomain;

  /**
   * Memoized callback function for handling order capture after user approval.
   * Passes the history object for navigation after successful payment.
   */
  const handleCaptureOrder = useCallback(
    (data, actions) => {
      return captureOrder(data, actions, history);
    },
    [history]
  );

  /**
   * Side-effect component that monitors PayPal SDK rejection state.
   * When SDK fails to load, it stops loading animation and hides Venmo button.
   * @returns {null} Renders nothing - purely for error handling side effects
   */
  const PayPalSDKErrorHandler = () => {
    // Extract the rejection state from PayPal SDK context
    const [{ isRejected }] = usePayPalScriptReducer();

    // Monitor SDK rejection state and update parent component accordingly
    useEffect(() => {
      if (isRejected) {
        // Stop loading animation since SDK failed to load
        setIsLoading(false);
        // Mark SDK as rejected to hide the Venmo payment option
        setIsSdkRejected(true);
      }
    }, [isRejected]); // Re-run effect only when isRejected state changes

    // This component doesn't render any UI - it's purely for side effects
    return null;
  };

  // Early return if user is not eligible for Venmo payments
  if (!isEligible || isSdkRejected) {
    return null;
  }

  return (
    <div className={`mb-16 mt-16 venmo-container${isLoading ? ' loading' : ''}`}>
      {/* PayPal SDK Provider with Venmo configuration */}
      <PayPalScriptProvider options={sdkOptions}>
        <PayPalSDKErrorHandler />
        {/* Show shimmer loading animation while SDK initializes */}
        {isLoading && <Shimmer />}

        {/* Payment button container - hidden during loading */}
        <div className='button-container'>
          <PayPalButtons
            fundingSource="venmo" // Specifically enable Venmo payments
            // No public prop type for this yet (JS SDK team guidance) - passes the
            // real merchant domain through so merchantRefererUrl is correct on
            // updateClientConfig instead of resolving to this paypal.com page.
            merchantDomain={merchantDomain}
            style={BUTTON_STYLE}
            onInit={(data, actions) => onInit(data, actions, setIsLoading)}
            onClick={() => validateDonationAmount() && onClick()} // Validate amount before proceeding
            onCancel={onCancel} // Handle user cancellation
            onError={(error) => onError(error, handleAlert)} // Handle payment errors
            createOrder={() => createOrder(handleAlert)} // Create order on button click
            onApprove={handleCaptureOrder} // Capture payment after user approval
          >
            {/* Component to handle ineligibility scenarios */}
            <InEligibleError setIsEligible={setIsEligible} />
          </PayPalButtons>
        </div>
      </PayPalScriptProvider>
    </div>
  );
});

DonateWithVenmo.displayName = 'DonateWithVenmo';

DonateWithVenmo.propTypes = {
  handleAlert: PropTypes.func,
  validateDonationAmount: PropTypes.func,
};

export default DonateWithVenmo;
